import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProduct } from '../api';
import { useCart } from '../context/CartContext';

import { FiShoppingCart, FiArrowLeft, FiCheck, FiZap } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './ProductDetail.css';

const NPR = (n) => 'NPR ' + parseFloat(n || 0).toLocaleString('en-NP');

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    getProduct(slug).then(({ data }) => { setProduct(data); setLoading(false); })
      .catch(() => { setLoading(false); navigate('/products'); });
  }, [slug, navigate]);

  const handleAddToCart = async () => {
    
    setAdding(true);
    try {
      await addItem(product, qty, product.price);
      toast.success('Added to cart!');
    } catch { toast.error('Error adding to cart'); }
    finally { setAdding(false); }
  };

  if (loading) return (
    <div className="pd-page container">
      <div className="pd-skeleton">
        <div className="skeleton" style={{ height: 500 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="skeleton" style={{ height: 40 }} />
          <div className="skeleton" style={{ height: 24, width: '60%' }} />
          <div className="skeleton" style={{ height: 80 }} />
        </div>
      </div>
    </div>
  );
  if (!product) return null;

  const images = product.images || [];
  const primaryImg = images.find((i) => i.is_primary) || images[0];
  const displayImages = images.length ? images : [];
  const currentImg = displayImages[activeImg];
  const inStock = product.inventory > 0;

  const specs = [
    { label: 'Max Flight Time', value: product.max_flight_time },
    { label: 'Max Speed', value: product.max_speed },
    { label: 'Camera Sensor', value: product.camera_sensor },
    { label: 'Video Resolution', value: product.video_res },
    { label: 'Transmission', value: product.transmission },
    { label: 'SKU', value: product.sku },
  ].filter((s) => s.value);

  return (
    <div className="pd-page">
      <div className="container">
        <button className="btn btn-ghost back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>

        <div className="pd-layout">
          {/* Gallery */}
          <div className="pd-gallery">
            <div className="pd-main-img">
              {currentImg ? (
                <img src={currentImg.image} alt={currentImg.alt || product.title} />
              ) : (
                <div className="pd-no-img"><FiZap /></div>
              )}
            </div>
            {displayImages.length > 1 && (
              <div className="pd-thumbs">
                {displayImages.map((img, i) => (
                  <button
                    key={img.id}
                    className={`pd-thumb ${activeImg === i ? 'active' : ''}`}
                    onClick={() => setActiveImg(i)}
                  >
                    <img src={img.image} alt={img.alt || ''} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="pd-info">
            {product.category && <span className="pd-category">{product.category}</span>}
            <h1 className="pd-title">{product.title}</h1>
            {product.sku && <p className="pd-sku text-muted">SKU: {product.sku}</p>}

            <div className="pd-price">{NPR(product.price)}</div>

            {/* Stock */}
            <div className={`pd-stock ${inStock ? 'in' : 'out'}`}>
              {inStock ? (
                <><FiCheck /> In Stock ({product.inventory} available)</>
              ) : (
                'Out of Stock'
              )}
            </div>

            {/* Description */}
            {product.description && (
              <div className="pd-desc">
                <p>{product.description}</p>
              </div>
            )}

            {/* Specs */}
            {specs.length > 0 && (
              <div className="pd-specs">
                <h3>Specifications</h3>
                <div className="specs-grid">
                  {specs.map((s) => (
                    <div key={s.label} className="spec-row">
                      <span className="spec-label">{s.label}</span>
                      <span className="spec-value">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add to Cart */}
            {inStock && (
              <div className="pd-actions">
                <div className="qty-control">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))}>−</button>
                  <span>{qty}</span>
                  <button onClick={() => setQty((q) => Math.min(product.inventory, q + 1))}>+</button>
                </div>
                <button
                  className="btn btn-primary btn-lg add-cart-btn"
                  onClick={handleAddToCart}
                  disabled={adding}
                >
                  <FiShoppingCart />
                  {adding ? 'Adding...' : 'Add to Cart'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;